/**
 * LikesStep.js — Assessment Wizard, Screen 8 "Likes"
 *
 * Captures up to MAX_LIKES preferred foods used to give candidate meals a
 * ranking boost in the AI Recommendation Layer — never an exclusion, and
 * never able to override a hard filter from Screen 7 (Diet Type, Cooking
 * Time, Budget) or the allergen/dislike exclusions from Screen 9. Built on
 * the shared AssessmentStepShell so progress bar, step counter, question
 * header, and Previous/Next footer stay identical to every other screen.
 *
 * Entirely optional per the FSD — Next is never disabled here, matching
 * the "Optional; max 15 selections" validation rule.
 *
 * Search suggestions are now shaped by the person's own Screen 7 answers
 * (Diet Type / Cuisine / Cooking Time / Budget), passed down as
 * `foodPreferences`: Diet Type / Cooking Time / Budget hard-exclude
 * incompatible catalog items from suggestions (mirrors the FSD's Screen 7
 * hard filters), and Cuisine soft-ranks matching items first (mirrors the
 * FSD's Screen 7 soft preference). See LikesStep.foodFilters.js.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useLikesAnalytics from "./useLikesAnalytics";
import { validateLikes } from "./LikesStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  FOOD_SEARCH_ITEMS,
  MAX_LIKES,
  ALLOW_CUSTOM_ENTRIES,
  LIKES_INITIAL_DATA,
} from "./LikesStep.constants";
import { filterAndRankFoodItems, describeActiveFilters } from "./LikesStep.foodFilters";
import {
  FieldGroup,
  FieldLabel,
  HelperValue,
  SearchInput,
  FilterNote,
  SuggestionGrid,
  SuggestionChip,
  SelectedGrid,
  SelectedChip,
  RemoveChipButton,
  EmptyState,
} from "./LikesStep.styles";

const STEP_NUMBER = STEP_NUMBERS.LIKES;

// Turns "Sweet Potato" into a stable-ish slug id for custom entries typed
// by the user (foods not in FOOD_SEARCH_ITEMS). Doesn't attempt to
// dedupe against the catalog's real ids beyond a simple slug match — good
// enough for a ranking-boost list, not a foreign key.
const slugify = (label) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * @param {object}   props
 * @param {string}   props.sessionId        - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData]    - Previously entered values, if the
 *   user navigated back to this step.
 * @param {object}   [props.foodPreferences] - Screen 7 "Food Preferences"
 *   answers, forwarded by the wizard: { dietType, cuisines, cookingTime,
 *   budget }. Used only to shape search suggestions here — Screen 7 itself
 *   remains the source of truth for hard-filtering the actual plan.
 * @param {function} props.onPrevious       - Go to Screen 7 (Food Preferences).
 * @param {function} props.onNext           - Go to Screen 9, receives
 *   { likes: string[] }.
 */
const LikesStep = ({ sessionId, initialData = null, foodPreferences = null, onPrevious, onNext }) => {
  const analytics = useLikesAnalytics();
  const [formData, setFormData] = useState({ ...LIKES_INITIAL_DATA, ...initialData });
  const [query, setQuery] = useState("");

  const { isValid, errors } = useMemo(() => validateLikes(formData), [formData]);
  const atCap = formData.likes.length >= MAX_LIKES;

  // Diet Type / Cooking Time / Budget hard-filtered, Cuisine soft-ranked —
  // recomputed only when the Screen 7 answers actually change.
  const catalogPool = useMemo(
    () => filterAndRankFoodItems(FOOD_SEARCH_ITEMS, foodPreferences),
    [foodPreferences]
  );

  const filterNote = useMemo(() => describeActiveFilters(foodPreferences), [foodPreferences]);

  const selectedLabelById = useMemo(() => {
    const map = new Map(FOOD_SEARCH_ITEMS.map((item) => [item.id, item.label]));
    // Custom entries store their own label as the id's title-cased form —
    // fall back to that when the id isn't in the catalog.
    return (id) => map.get(id) ?? id.replace(/_/g, " ");
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return catalogPool
      .filter((item) => item.label.toLowerCase().includes(q) && !formData.likes.includes(item.id))
      .slice(0, 8);
  }, [query, formData.likes, catalogPool]);

  const addLike = useCallback(
    (foodId) => {
      if (atCap || formData.likes.includes(foodId)) return;
      setFormData((prev) => ({ ...prev, likes: [...prev.likes, foodId] }));
      analytics.trackLikeAdd(sessionId, foodId);
      setQuery("");
    },
    [atCap, formData.likes, analytics, sessionId]
  );

  const removeLike = useCallback(
    (foodId) => {
      setFormData((prev) => ({ ...prev, likes: prev.likes.filter((id) => id !== foodId) }));
      analytics.trackLikeRemove(sessionId, foodId);
    },
    [analytics, sessionId]
  );

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleQueryKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const trimmed = query.trim();
      if (!trimmed || atCap) return;

      // Exact catalog match takes priority over minting a custom id.
      // Matched against catalogPool (not the raw catalog) so a food hidden
      // by the person's own diet/cooking-time/budget answers can't be
      // added via an exact-label Enter either — free typing still falls
      // through to a custom entry below, which is intentionally not
      // filtered (see LikesStep.constants.js).
      const catalogMatch = catalogPool.find(
        (item) => item.label.toLowerCase() === trimmed.toLowerCase()
      );
      if (catalogMatch) {
        addLike(catalogMatch.id);
        return;
      }
      if (ALLOW_CUSTOM_ENTRIES) {
        addLike(slugify(trimmed));
      }
    },
    [query, atCap, addLike, catalogPool]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Likes (Screen 8) returns to Food Preferences (Screen 7).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Likes is optional, this only fires if something
      // manages to push more than MAX_LIKES entries in programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData);
    // Advances the wizard to Screen 9 (Dislikes).
    onNext?.(formData);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="What do you like to eat?"
      description="We'll prioritize these when picking recipes for you — optional, pick up to 15."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel htmlFor="likesSearch">
          Search foods{" "}
          <HelperValue>
            ({formData.likes.length}/{MAX_LIKES} selected)
          </HelperValue>
        </FieldLabel>
        <SearchInput
          id="likesSearch"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleQueryKeyDown}
          placeholder={atCap ? "Limit reached — remove one to add another" : "e.g. paneer, mango, biryani…"}
          disabled={atCap}
          autoComplete="off"
        />
        {filterNote && <FilterNote>{filterNote}</FilterNote>}
        {suggestions.length > 0 && (
          <SuggestionGrid>
            {suggestions.map((item) => (
              <SuggestionChip key={item.id} type="button" onClick={() => addLike(item.id)} disabled={atCap}>
                + {item.label}
              </SuggestionChip>
            ))}
          </SuggestionGrid>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel id="likesSelectedLabel">Selected</FieldLabel>
        <SelectedGrid role="group" aria-labelledby="likesSelectedLabel">
          {formData.likes.length === 0 ? (
            <EmptyState>Nothing selected yet.</EmptyState>
          ) : (
            formData.likes.map((id) => (
              <SelectedChip key={id}>
                {selectedLabelById(id)}
                <RemoveChipButton
                  type="button"
                  aria-label={`Remove ${selectedLabelById(id)}`}
                  onClick={() => removeLike(id)}
                >
                  ×
                </RemoveChipButton>
              </SelectedChip>
            ))
          )}
        </SelectedGrid>
      </FieldGroup>
    </AssessmentStepShell>
  );
};

LikesStep.displayName = "LikesStep";
export default React.memo(LikesStep);